/**
 * This component represents a dialog for adding a price configuration.
 * It allows the user to select an Independent System Operator, choose a power purchase agreement type,
 * select a prize zone, and review the form before submission.
 *
 * @component
 * @example
 * return (
 *   <AddPriceConfigDialog />
 * )
 */
import React, { use, useEffect } from "react";
import { useTheme } from "../../../ThemeContext/ThemeContext.js";
import {
  StyledIconButton,
  PrimaryButton,
  SecondaryButton,
  StyledRadio,
  OutlinedButton,
} from "../../../components/StyledComponents/Buttons/AuradineButtons.js";

import { useState } from "react";
import { StyledDialog } from "../../../components/StyledComponents/Dialog/Dialog.js";
import {
  BodyLarge,
  BodySmall,
  BodyMedium,
  BodyXSmall,
  H6HeadlineSmall,
  H8TitleMedium,
  H9TitleSmall,
} from "../../../components/StyledComponents/Typography/Typography.tsx";
import {
  Autocomplete,
  DialogActions,
  DialogContent,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  styled,
  TextField,
  Tooltip,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import CloseIcon from "@mui/icons-material/Close";
import { Box } from "@mui/material";
import useAppContextProvider from "../../../AppContext/useAppContextProvider.js";
import usePriceConfigurationContext from "../../context/usePriceConfigurationContext.js";
import PlaceHolderButton from "../../../components/PlaceHolderButton/PlaceHolderButton.js";
import StyledLinearProgress from "../../../components/StyledComponents/Progress/Progress.js";
import { StyledMenuItem } from "../../../components/StyledComponents/Menu/Menu.tsx";
import {
  StyledCheckbox,
  StyledFormControl,
  StyledInputLabel,
  StyledOutlinedInput,
  StyledSelect,
  StyledTextField,
} from "../../../components/StyledComponents/Inputs/Inputs.js";
import { tooltipClasses } from "@mui/material/Tooltip";

import { useAuth0 } from "@auth0/auth0-react";
import { useGetSiteLists } from "../../../api/queries/useGetSiteLists.ts";
import { useAddPriceConfig } from "../../queries/usePostPriceConfig.ts";
import { useAttachPriceConfig } from "../../queries/useAttachPriceConfig.ts";
import { useQuery } from "@tanstack/react-query";
import {
  PostAutoTuneV2Config,
  GetEnergyPrice,
} from "../../../api/api.js";
import DataGrid from "../../../components/DataGrid/DataGrid.tsx";
import PriceConfigSitesDialog from "../PriceConfigSitesDialog/PriceConfigSitesDialog.js";
import { isEmpty, set } from "lodash";
import { da, fi, is } from "date-fns/locale";
import StyledSwitch from "../../../components/StyledComponents/Switch/Switch.js";
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme, colors }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: colors.background.paper, 
    color: colors.text.primary,
    maxWidth: "25rem",
    fontSize: theme.typography.pxToRem(12),
    border: "1px solid " + colors.border.default,
    borderRadius: "2rem",
    padding: "0.5rem",
  },
}));

const PriceConfigurationFormHeader = (props) => {
  const { setFormData } = props;
  const {
    setOpenAddPriceConfigModal,
    setActiveStep,
    setTotalSteps,
    setSiteConfigurationDataToEdit,
    setEditConfigModeEnabled,
    editConfigModeEnabled,
    setFluxVisionTuning,
  } = usePriceConfigurationContext();
  const { colors } = useTheme();
  return (
    <Box
      display={"flex"}
      flexDirection={"row"}
      justifyContent={"space-between"}
      flexGrow={1}
      padding={"1rem 0"}
    >
      <H6HeadlineSmall>
        {!editConfigModeEnabled
          ? `Create a New Price Configuration`
          : `Edit Price Configuration`}
      </H6HeadlineSmall>
      <StyledIconButton
        colors={colors}
        onClick={() => {
          setOpenAddPriceConfigModal(false);
          setFormData({});
          setActiveStep(0);
          setTotalSteps(4);
          setSiteConfigurationDataToEdit(null);
          setEditConfigModeEnabled(false);
          setFluxVisionTuning(true);
        }}
      >
        <CloseIcon  />
      </StyledIconButton>
    </Box>
  );
};

const SystemOperatorForm = ({
  onNext,
  formData,
  setFormData,
  setActiveStep,
}) => {
  const { colors } = useTheme();
  const {
    priceConfig,
    setTotalSteps,
    siteConfigurationDataToEdit,
    editConfigModeEnabled,
    authToken,
    setAttachedSites,
    setResetForms,
    setSiteConfigurationDataToEdit,
  } = usePriceConfigurationContext();

  const {
    data: autoTuneV2ConfigData,
    isFetching: isAutoTuneFetching,
    isFetched: isAutoTuneFetched,
    refetch: autoTuneV2ConfigRefetch,
  } = useQuery({
    queryKey: [
      "autoTuneV2ConfigData",
      editConfigModeEnabled,
    ],
    queryFn: () =>
      PostAutoTuneV2Config(
        authToken,
        {
          config_type: "priceConfig",
          config_name:
            siteConfigurationDataToEdit?.priceConfigName,
        },
        "GET"
      ),
    enabled: !!authToken && editConfigModeEnabled,
  });
  const siteListsFromAutoTuneV2ConfigData =
    autoTuneV2ConfigData &&
    autoTuneV2ConfigData.siteList &&
    autoTuneV2ConfigData?.siteList?.length > 0
      ? autoTuneV2ConfigData?.siteList
      : [];

  const { control, handleSubmit, watch, setValue, reset } =
    useForm({
      defaultValues: {
        ISO: formData?.ISO || "",
      },
    });

  useEffect(() => {
    if (siteConfigurationDataToEdit) {
      setValue("ISO", siteConfigurationDataToEdit?.ISO);
    }
  }, [siteConfigurationDataToEdit]);

  useEffect(() => {
    if (formData?.ISO) {
      setValue("ISO", formData.ISO);
      setFormData((prev) => ({
        ...prev,
        ISO: formData.ISO,
      }));
    }
  }, [formData?.ISO, setValue]);

  const selectedOption = watch("ISO");

  const onSubmit = (data) => {
    const finalData =
      selectedOption &&
      selectedOption?.toLowerCase() === "ercot"
        ? { ...data }
        : { PPAType: "Hedge", ...data };
    const totalSteps =
      selectedOption &&
      selectedOption?.toLowerCase() === "none"
        ? 3
        : 4;
    setTotalSteps(totalSteps);
    setResetForms((prev) => [...prev, reset]);
    if (editConfigModeEnabled) {
      setSiteConfigurationDataToEdit((prev) => ({
        ...prev,
        ISO:
          prev?.ISO !== data?.ISO ? data?.ISO : prev?.ISO,
        siteList: siteListsFromAutoTuneV2ConfigData,
      }));
    }
    onNext(finalData);
  };
  return (
    <>
      <PriceConfigurationFormHeader
        setFormData={setFormData}
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"2.5rem"}
          paddingBottom={"2.5rem"}
        >
          <H8TitleMedium>
            {" "}
            Select an Independent System Operator
          </H8TitleMedium>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors} component="fieldset">
              <Controller
                name="ISO"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="ERCOT"
                        control={<StyledRadio colors={colors} />}
                        label="ERCOT"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="NONE"
                        control={<StyledRadio colors={colors} />}
                        label="None"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
        </Box>
      </DialogContent>
      <DialogActions>
        {editConfigModeEnabled && (
          <OutlinedButton
            colors={colors}
            disabled={!isAutoTuneFetched}
            onClick={() => {
              setFormData((prev) => ({
                ...prev,
                ...siteConfigurationDataToEdit,
                siteList: siteListsFromAutoTuneV2ConfigData,
              }));
              setSiteConfigurationDataToEdit((prev) => ({
                ...prev,
                ...siteConfigurationDataToEdit,
                siteList: siteListsFromAutoTuneV2ConfigData,
              }));
              const totalSteps =
                siteConfigurationDataToEdit.ISO &&
                siteConfigurationDataToEdit.ISO?.toLowerCase() ===
                  "ercot"
                  ? 4
                  : 3;
              setTotalSteps(totalSteps);
              setActiveStep(totalSteps - 1);
            }}
          >
            Skip to review form
          </OutlinedButton>
        )}
        <PrimaryButton colors={colors}
          disabled={!selectedOption}
          onClick={handleSubmit(onSubmit)}
        >
          Next
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const PowerPurchaseAgreementForm = ({
  formData,
  onNext,
  onBack,
  setFormData,
}) => {
  const { colors } = useTheme();
  const {
    priceConfig,
    siteConfigurationDataToEdit,
    setResetForms,
    resetForms,
    editConfigModeEnabled,
    setSiteConfigurationDataToEdit,
  } = usePriceConfigurationContext();
  const { control, handleSubmit, watch, setValue, reset } =
    useForm({
      defaultValues: {
        PPAType:
          formData?.PPAType || priceConfig?.PPAType || "",
      },
    });

  useEffect(() => {
    if (
      siteConfigurationDataToEdit &&
      siteConfigurationDataToEdit.PPAType !== undefined
    ) {
      reset({
        PPAType: siteConfigurationDataToEdit.PPAType,
      });
    } else {
      reset({
        PPAType: formData?.PPAType || "",
      });
    }
  }, [siteConfigurationDataToEdit, formData, reset]);

  const onSubmit = (data) => {
    setResetForms((prev) => [...prev, reset]);
    if (editConfigModeEnabled) {
      setFormData((prev) => ({
        ...prev,
        PPAType:
          prev.PPAType !== data.PPAType
            ? data.PPAType
            : prev.PPAType,
      }));
      setSiteConfigurationDataToEdit((prev) => ({
        ...prev,
        PPAType:
          prev.PPAType !== data.PPAType
            ? data.PPAType
            : prev.PPAType,
      }));
    }
    onNext(data);
  };

  const PPAType = watch("PPAType");
  return (
    <>
      <PriceConfigurationFormHeader
        setFormData={setFormData}
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
          paddingTop={"2.5rem"}
          paddingBottom={"2.5rem"}
        >
          <H8TitleMedium>
            {" "}
            Choose your Power Purchase Agreement type.
          </H8TitleMedium>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledFormControl colors={colors} component="fieldset">
              <Controller
                name="PPAType"
                control={control}
                rules={{
                  required: "Please select an option",
                }}
                render={({
                  field,
                  fieldState: { error },
                }) => (
                  <>
                    <RadioGroup {...field}>
                      <FormControlLabel
                        value="Index"
                        control={<StyledRadio colors={colors} />}
                        label="Index"
                        sx={{ color: colors.text.primary }}
                      />
                      <FormControlLabel
                        value="Hedge"
                        control={<StyledRadio colors={colors} />}
                        label="Hedge"
                        sx={{ color: colors.text.primary }}
                      />
                    </RadioGroup>
                  </>
                )}
              />
            </StyledFormControl>
          </form>
        </Box>
      </DialogContent>

      <DialogActions>
        <SecondaryButton colors={colors} onClick={onBack}>
          Back
        </SecondaryButton>
        <PrimaryButton colors={colors}
          disabled={!PPAType}
          onClick={handleSubmit(onSubmit)}
        >
          Next
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const PriceZoneForm = ({
  formData,
  onNext,
  onBack,
  setFormData,
  setEnergyPricePriceZone,
}) => {
  const {
    priceConfig,
    siteConfigurationDataToEdit,
    setResetForms,
    resetForms,
    authToken,
    editConfigModeEnabled,
    setSiteConfigurationDataToEdit,
  } = usePriceConfigurationContext();
  const { colors } = useTheme();
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    mode: "onChange",
    defaultValues: {
      PriceZone:
        priceConfig?.PriceZone || formData?.PriceZone || "",
      EnergyPrice:
        priceConfig?.EnergyPrice || formData?.EnergyPrice,
      TunePreference:
        priceConfig?.TunePreference ||
        formData?.TunePreference ||
        "",
      EnergyPriceLowerThreshold:
        priceConfig?.EnergyPriceLowerThreshold ||
        formData?.EnergyPriceLowerThreshold,
      EnergyPriceUpperThreshold:
        priceConfig?.EnergyPriceUpperThreshold ||
        formData?.EnergyPriceUpperThreshold,
      RampUpTime:
        priceConfig?.RampUpTime || formData?.RampUpTime,
      RampDownTime:
        priceConfig?.RampDownTime || formData?.RampDownTime,
    },
  });

  useEffect(() => {
    reset({
      PriceZone:
        siteConfigurationDataToEdit?.PriceZone ??
        formData?.PriceZone ??
        "",
      EnergyPrice:
        siteConfigurationDataToEdit?.EnergyPrice ??
        formData?.EnergyPrice ??
        "",
      TunePreference:
        siteConfigurationDataToEdit?.TunePreference ??
        formData?.TunePreference ??
        "",
      EnergyPriceLowerThreshold:
        siteConfigurationDataToEdit?.EnergyPriceLowerThreshold ??
        formData?.EnergyPriceLowerThreshold ??
        "",
      EnergyPriceUpperThreshold:
        siteConfigurationDataToEdit?.EnergyPriceUpperThreshold ??
        formData?.EnergyPriceUpperThreshold ??
        "",
      RampUpTime:
        siteConfigurationDataToEdit?.RampUpTime ??
        formData?.RampUpTime ??
        "",
      RampDownTime:
        siteConfigurationDataToEdit?.RampDownTime ??
        formData?.RampDownTime ??
        "",
    });
  }, [
    siteConfigurationDataToEdit?.PriceZone,
    siteConfigurationDataToEdit?.EnergyPrice,
    siteConfigurationDataToEdit?.TunePreference,
    siteConfigurationDataToEdit?.EnergyPriceLowerThreshold,
    siteConfigurationDataToEdit?.EnergyPriceUpperThreshold,
    siteConfigurationDataToEdit?.RampUpTime,
    siteConfigurationDataToEdit?.RampDownTime,
    formData?.PriceZone,
    formData?.EnergyPrice,
    formData?.TunePreference,
    formData?.EnergyPriceLowerThreshold,
    formData?.EnergyPriceUpperThreshold,
    formData?.RampUpTime,
    formData?.RampDownTime,
    reset,
  ]);

  const { PPAType } = formData;
  const onSubmit = (data) => {
    const finalData = {
      ...data,
    };
    setResetForms((prev) => [...prev, reset]);
    if (editConfigModeEnabled) {
      setFormData((prev) => ({
        ...prev,
        ...data,
      }));
      setSiteConfigurationDataToEdit((prev) => ({
        ...prev,
        ...data,
      }));
    }
    onNext(finalData);
  };

  const selectedOption =
    watch("PriceZone") || watch("EnergyPrice");

  const costOfElectricityObserver = watch("EnergyPrice");

  const tunrOnPriceObserver = watch(
    "EnergyPriceLowerThreshold"
  );

  const turnOffPriceObserver = watch(
    "EnergyPriceUpperThreshold"
  );
  const rampUpTimeObserver = watch("RampUpTime");
  const rampDownTimeObserver = watch("RampDownTime");

  const tunePreference = watch("TunePreference");

  const {
    data: energyPriceData,
    isFetching: isEnergyPriceFetching,
    isFetched: isEnergyPriceFetched,
    refetch: energyPriceRefetch,
  } = useQuery({
    queryKey: [
      "energyPriceData",
      selectedOption,
      siteConfigurationDataToEdit.PriceZone,
    ],
    queryFn: () =>
      GetEnergyPrice(
        authToken,
        {
          ISO: editConfigModeEnabled
            ? siteConfigurationDataToEdit.ISO
            : formData.ISO,
          priceZone: editConfigModeEnabled
            ? selectedOption ===
              siteConfigurationDataToEdit.PriceZone
              ? siteConfigurationDataToEdit.PriceZone
              : selectedOption
            : selectedOption,
        },
        "GET"
      ),
    enabled:
      !!authToken && editConfigModeEnabled
        ? !!(
            (siteConfigurationDataToEdit.ISO &&
              siteConfigurationDataToEdit.PriceZone) ||
            (siteConfigurationDataToEdit.ISO &&
              selectedOption)
          )
        : !!(formData.ISO && selectedOption),
  });

  const energyPricefromGetEnergyPrice =
    energyPriceData &&
    energyPriceData.status === "ok" &&
    energyPriceData.data &&
    energyPriceData.data.ERCOT &&
    energyPriceData.data.ERCOT[
      Object.keys(energyPriceData.data.ERCOT)[0]
    ].energyPrice &&
    energyPriceData.data.ERCOT[
      Object.keys(energyPriceData.data.ERCOT)[0]
    ].energyPrice !== ""
      ? energyPriceData.data.ERCOT[
          Object.keys(energyPriceData.data.ERCOT)[0]
        ].energyPrice
      : "";

  React.useEffect(() => {
    if (energyPricefromGetEnergyPrice) {
      setEnergyPricePriceZone(
        energyPricefromGetEnergyPrice
      );
    }
  }, [energyPricefromGetEnergyPrice]);

  const energyPriceUpdatedfromGetEnergyPrice =
    energyPriceData &&
    energyPriceData.status === "ok" &&
    energyPriceData.data &&
    energyPriceData.data.ERCOT &&
    energyPriceData.data.ERCOT[
      Object.keys(energyPriceData.data.ERCOT)[0]
    ].ERCOTupdatedAt &&
    energyPriceData.data.ERCOT[
      Object.keys(energyPriceData.data.ERCOT)[0]
    ].ERCOTupdatedAt !== ""
      ? energyPriceData.data.ERCOT[
          Object.keys(energyPriceData.data.ERCOT)[0]
        ].ERCOTupdatedAt
      : "";

  return (
    <>
      <PriceConfigurationFormHeader
        setFormData={setFormData}
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={4}
        >
          {PPAType &&
            PPAType?.toLowerCase() === "index" && (
              <H8TitleMedium>
                {" "}
                Select your price zone
              </H8TitleMedium>
            )}
          {PPAType && PPAType.toLowerCase() === "hedge" && (
            <H8TitleMedium>
              Enter your cost of electricity (Per MW Per
              Hour)
            </H8TitleMedium>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            {PPAType &&
              PPAType?.toLowerCase() === "index" && (
                <Box
                  display={"grid"}
                  gridTemplateRows={"auto auto auto auto"}
                  gap={6}
                >
                  <StyledFormControl colors={colors}
                    fullWidth
                    margin="none"
                  >
                    <StyledInputLabel colors={colors}>
                      Price zone
                    </StyledInputLabel>
                    <Controller
                      name="PriceZone"
                      control={control}
                      rules={{
                        required:
                          "Please select a price zone",
                      }}
                      render={({ field }) => (
                        <StyledSelect
                          {...field}
                          sx={{ minHeight: "3.5rem" }}
                          variant="outlined"
                          label="Price Zone"
                          colors={colors}
                          fullWidth
                          placeholder="Price Zone"
                          inputProps={{
                            sx: {
                              padding: "1rem 0.5rem",
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: "250px",
                                overflowY: "auto",
                                backgroundColor: colors.background.paper,
                                color: colors.text.primary,
                              },
                            },
                          }}
                          onChange={(event) =>
                            field.onChange(
                              event.target.value
                            )
                          }
                        >
                          <StyledMenuItem
                            colors={colors}
                            value={"HB_HOUSTON"}
                          >
                            HB_HOUSTON
                          </StyledMenuItem>
                          <StyledMenuItem
                            colors={colors}
                            value={"HB_NORTH"}
                          >
                            HB_NORTH
                          </StyledMenuItem>
                          <StyledMenuItem value={"HB_PAN"} colors={colors}>
                            HB_PAN
                          </StyledMenuItem>
                          <StyledMenuItem
                            colors={colors}
                            value={"HB_SOUTH"}
                          >
                            HB_SOUTH
                          </StyledMenuItem>
                          <StyledMenuItem value={"HB_WEST"} colors={colors}>
                            HB_WEST
                          </StyledMenuItem>
                          <StyledMenuItem value={"LZ_AEN"} colors={colors}>
                            LZ_AEN
                          </StyledMenuItem>
                          <StyledMenuItem value={"LZ_CPS"} colors={colors}>
                            LZ_CPS
                          </StyledMenuItem>
                          <StyledMenuItem
                            colors={colors}
                            value={"LZ_HOUSTON"}
                          >
                            LZ_HOUSTON
                          </StyledMenuItem>
                          <StyledMenuItem value={"LZ_LCRA"} colors={colors}>
                            LZ_LCRA
                          </StyledMenuItem>
                          <StyledMenuItem
                            colors={colors}
                            value={"LZ_NORTH"}
                          >
                            LZ_NORTH
                          </StyledMenuItem>
                          <StyledMenuItem
                            colors={colors}
                            value={"LZ_SOUTH"}
                          >
                            LZ_SOUTH
                          </StyledMenuItem>
                          <StyledMenuItem value={"LZ_WEST"} colors={colors}>
                            LZ_WEST
                          </StyledMenuItem>
                        </StyledSelect>
                      )}
                    />
                  </StyledFormControl>
                  {selectedOption &&
                    energyPriceUpdatedfromGetEnergyPrice &&
                    energyPricefromGetEnergyPrice && (
                      <>
                        <Stack direction={"row"} gap={2}>
                          <H8TitleMedium>
                            Last 5 Minutes Real-time Zone
                            Strike Price
                          </H8TitleMedium>
                          <H8TitleMedium
                            sx={{ fontWeight: 400 }}
                          >
                            [
                            {
                              energyPriceUpdatedfromGetEnergyPrice
                            }{" "}
                            Central Time]
                          </H8TitleMedium>
                        </Stack>
                        <Stack
                          direction={"row"}
                          gap={3}
                          display={"flex"}
                          alignItems={"center"}
                        >
                          <BodyLarge>
                            {energyPricefromGetEnergyPrice}
                          </BodyLarge>
                          <SecondaryButton colors={colors}
                            onClick={() =>
                              energyPriceRefetch()
                            }
                          >
                            Get Latest Price
                          </SecondaryButton>
                        </Stack>
                      </>
                    )}
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    gap={4}
                  >
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      gap={2}
                    >
                      <H8TitleMedium>
                        Tune Preference
                      </H8TitleMedium>
                      <HtmlTooltip
                        colors={colors}
                        placement="right"
                        title={
                          <>
                            <Box
                              display={"flex"}
                              flexDirection={"column"}
                              gap={2}
                              padding={2}
                              flexWrap={"wrap"}
                            >
                              <BodySmall>
                                <strong> Manual </strong> –
                                Calculates THs once using
                                current energy prices and
                                BTC data. No automatic
                                updates.
                              </BodySmall>
                              <BodySmall>
                                <strong>Auto </strong> –
                                Automatically calculates
                                optimal THs (terahashes per
                                second) every hour based on
                                real-time energy prices and
                                BTC value.
                              </BodySmall>
                              <BodySmall>
                                <strong>
                                  Energy Optimizer{" "}
                                </strong>{" "}
                                – Dynamically manages miner
                                activity:
                              </BodySmall>
                              <BodySmall>
                                Resumes miners from Standby
                                when energy prices drop
                                below the "Turn On"
                                threshold.
                              </BodySmall>
                              <BodySmall>
                                Puts miners into Standby
                                when prices exceed the "Turn
                                Off" threshold.
                              </BodySmall>
                            </Box>
                          </>
                        }
                      >
                        <span className="material-symbols-outlined " style={{ color: colors.text.muted }}>
                          info
                        </span>
                      </HtmlTooltip>
                    </Box>
                    <StyledFormControl colors={colors} component="fieldset">
                      <Controller
                        name="TunePreference"
                        control={control}
                        rules={{
                          required:
                            "Please select an option",
                        }}
                        render={({
                          field,
                          fieldState: { error },
                        }) => (
                          <>
                            <RadioGroup {...field}>
                              <FormControlLabel
                                value="Manual"
                                control={<StyledRadio colors={colors} />}
                                label="Manual"  
                                sx={{ color: colors.text.primary }}
                              />
                              <FormControlLabel
                                value="Auto"
                                control={<StyledRadio colors={colors} />}
                                label="Auto"
                                sx={{ color: colors.text.primary }}
                              />
                              <FormControlLabel
                                value="Energy Optimizer"
                                control={<StyledRadio colors={colors} />}
                                label="Energy Optimizer"
                                sx={{ color: colors.text.primary }}
                              />
                            </RadioGroup>
                          </>
                        )}
                      />
                    </StyledFormControl>
                  </Box>

                  {tunePreference ===
                    "Energy Optimizer" && (
                    <Box
                      display="flex"
                      flexDirection={"column"}
                      gap={8}
                    >
                      <Box
                        display={"grid"}
                        gridTemplateRows={"auto auto"}
                        gap={4}
                      >
                        <H8TitleMedium>
                          Turn on price
                        </H8TitleMedium>
                        <Controller
                          name="EnergyPriceLowerThreshold"
                          control={control}
                          rules={{
                            required:
                              "Please enter a value",
                            validate: (value) => {
                              const number = Number(value);
                              const turnOff = Number(
                                turnOffPriceObserver
                              );
                              console.log(
                                turnOff,
                                "turnOff"
                              );

                              // Only compare if turn off price is set and not empty
                              if (
                                costOfElectricityObserver !==
                                  "" &&
                                !isNaN(turnOff)
                              ) {
                                if (number > turnOff) {
                                  return "Turn on price must be less than turn off price";
                                }
                              }
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <StyledTextField colors={colors}
                              margin="none"
                              variant="outlined"
                              type="number"
                              label="Electricity Cost per MWh (in USD)"
                              {...field}
                              error={Boolean(
                                errors.EnergyPriceLowerThreshold
                              )}
                              onChange={(event) => {
                                field.onChange(
                                  event.target.value
                                );
                                if (
                                  !errors.EnergyPriceUpperThreshold
                                ) {
                                  trigger(
                                    "EnergyPriceLowerThreshold"
                                  );
                                } else {
                                  trigger([
                                    "EnergyPriceUpperThreshold",
                                    "EnergyPriceLowerThreshold",
                                  ]);
                                }
                              }}
                              onBlur={(event) => {
                                // Format to two decimals on blur
                                let value =
                                  event.target.value;
                                if (
                                  value !== "" &&
                                  !isNaN(Number(value))
                                ) {
                                  console.log(
                                    value,
                                    "value"
                                  );

                                  value =
                                    parseFloat(
                                      value
                                    ).toFixed(2);
                                  field.onChange(value);
                                  trigger(
                                    "EnergyPriceUpperThreshold"
                                  );
                                  trigger(
                                    "EnergyPriceLowerThreshold"
                                  );
                                }
                              }}
                              helperText={
                                errors
                                  .EnergyPriceLowerThreshold
                                  ?.message
                                  ? errors
                                      .EnergyPriceLowerThreshold
                                      ?.message
                                  : "Resume miners from Standby if zone strike price drops below turn on price"
                              }
                            />
                          )}
                        />
                        <Controller
                          name="RampUpTime"
                          control={control}
                          rules={{
                            required:
                              "Please enter a value",
                            validate: (value) => {
                              if (
                                value < 1 ||
                                value > 3600
                              ) {
                                return "Ramp up time must be between 1 and 3600 seconds";
                              }
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <StyledTextField colors={colors}
                              margin="none"
                              variant="outlined"
                              label="Ramp Up Time"
                              type="number"
                              {...field}
                              error={Boolean(
                                errors.RampUpTime
                              )}
                              helperText={
                                "Ramp up time must be between 1 and 3600 seconds"
                              }
                            />
                          )}
                        />
                      </Box>
                      <Box
                        display={"grid"}
                        gridTemplateRows={"auto auto"}
                        gap={4}
                      >
                        <H8TitleMedium>
                          Turn off price
                        </H8TitleMedium>
                        <Controller
                          name="EnergyPriceUpperThreshold"
                          rules={{
                            required:
                              "Please enter a value",
                            validate: (value) => {
                              const number = Number(value);
                              const turnOn = Number(
                                tunrOnPriceObserver
                              );
                              if (
                                tunrOnPriceObserver !==
                                  "" &&
                                !isNaN(turnOn) &&
                                value !== "" &&
                                !isNaN(number)
                              ) {
                                if (number < turnOn) {
                                  return "Turn off price must be greater than turn on price";
                                }
                              }
                              return true;
                            },
                          }}
                          control={control}
                          render={({ field }) => (
                            <StyledTextField colors={colors}
                              margin="none"
                              variant="outlined"
                              label="Electricity Cost per MWh (in USD)"
                              type="number"
                              {...field}
                              error={Boolean(
                                errors.EnergyPriceUpperThreshold
                              )}
                              onChange={(event) => {
                                field.onChange(
                                  event.target.value
                                );
                                if (
                                  !errors.EnergyPriceLowerThreshold
                                ) {
                                  trigger(
                                    "EnergyPriceUpperThreshold"
                                  );
                                } else {
                                  trigger([
                                    "EnergyPriceUpperThreshold",
                                    "EnergyPriceLowerThreshold",
                                  ]);
                                }
                              }}
                              onBlur={(event) => {
                                // Format to two decimals on blur
                                let value =
                                  event.target.value;
                                if (
                                  value !== "" &&
                                  !isNaN(Number(value))
                                ) {
                                  value =
                                    parseFloat(
                                      value
                                    ).toFixed(2);
                                  field.onChange(value);
                                  trigger(
                                    "EnergyPriceUpperThreshold"
                                  );
                                  trigger(
                                    "EnergyPriceLowerThreshold"
                                  );
                                }
                              }}
                              helperText={
                                errors
                                  .EnergyPriceUpperThreshold
                                  ?.message
                                  ? errors
                                      .EnergyPriceUpperThreshold
                                      ?.message
                                  : "Put miners on Standby if zone strike price exceeds turn off price"
                              }
                            />
                          )}
                        />
                        <Controller
                          name="RampDownTime"
                          control={control}
                          rules={{
                            required:
                              "Please enter a value",
                            validate: (value) => {
                              const number = Number(value);
                              if (!isNaN(number)) {
                                if (
                                  number < 1 ||
                                  number > 900
                                ) {
                                  return "Ramp down time must be between 1 and 900 seconds";
                                }
                              }

                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <StyledTextField colors={colors}
                              margin="none"
                              variant="outlined"
                              label="Ramp Down Time"
                              type="number"
                              {...field}
                              error={Boolean(
                                errors.RampDownTime
                              )}
                              helperText={
                                errors.RampDownTime?.message
                                  ? errors.RampDownTime
                                      ?.message
                                  : "Ramp down time must be between 1 and 900 seconds"
                              }
                            />
                          )}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              )}

            {PPAType &&
              PPAType?.toLowerCase() === "hedge" && (
                <Box
                  display={"flex"}
                  flexDirection={"column"}
                  gap={6}
                >
                  <StyledFormControl colors={colors}
                    fullWidth
                    margin="none"
                  >
                    {/*<InputLabel>Status</InputLabel>*/}
                    <InputLabel htmlFor="cost-of-electricity">
                      In USD
                    </InputLabel>
                    <Controller
                      name="EnergyPrice"
                      control={control}
                      rules={{
                        required: "Please enter a value",
                        validate: (value) => {
                          if (value < 0) {
                            return "Negative values are not allowed";
                          } else if (value > 5000) {
                            return "Value must be less than or equal to 5000 USD per MW per hour";
                          }
                          return true;
                        },
                      }}
                      defaultValue={null}
                      render={({ field }) => (
                        <>
                          <StyledOutlinedInput
                            colors={colors}
                            {...field}
                            label="In USD"
                            type="number"
                            id="cost-of-electricity"
                            onBlur={(event) => {
                              // Format to two decimals on blur
                              let value =
                                event.target.value;
                              if (
                                value !== "" &&
                                !isNaN(Number(value))
                              ) {
                                value =
                                  parseFloat(value).toFixed(
                                    2
                                  );
                                field.onChange(value);
                              }
                            }}
                            style={{
                              flex: 1,
                              margin: 0,
                              minHeight: "2.5rem",
                              // padding: "16.5px 14px",
                            }}
                            error={Boolean(
                              errors.EnergyPrice
                            )}
                          />
                          {errors.EnergyPrice?.message && (
                            <Box
                              sx={{
                                color: "red",
                                fontSize: "0.75rem",
                                mt: 0.5,
                                ml: 2,
                              }}
                            >
                              <BodySmall
                                sx={{ color: "red" }}
                              >
                                {
                                  errors.EnergyPrice
                                    ?.message
                                }{" "}
                              </BodySmall>
                            </Box>
                          )}
                        </>
                      )}
                    />
                  </StyledFormControl>
                  <Box
                    display={"flex"}
                    flexDirection={"column"}
                    gap={4}
                  >
                    <H8TitleMedium>
                      Tune Preference
                    </H8TitleMedium>
                    <StyledFormControl colors={colors} component="fieldset">
                      <Controller
                        name="TunePreference"
                        control={control}
                        rules={{
                          required:
                            "Please select an option",
                        }}
                        render={({
                          field,
                          fieldState: { error },
                        }) => (
                          <>
                            <RadioGroup {...field}>
                              <FormControlLabel
                                value="Manual"
                                control={<StyledRadio colors={colors} />}
                                label="Manual"
                                sx={{ color: colors.text.primary }}
                              />
                              <FormControlLabel
                                value="Auto"
                                control={<StyledRadio colors={colors} />}
                                label="Auto"
                                sx={{ color: colors.text.primary }}
                              />
                            </RadioGroup>
                          </>
                        )}
                      />
                    </StyledFormControl>
                  </Box>
                </Box>
              )}
          </form>
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton colors={colors} onClick={onBack}>
          Back
        </SecondaryButton>
        <PrimaryButton colors={colors}
          disabled={!isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Next
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const ReviewForm = ({
  formData,
  onSubmit: onOverallSubmit,
  onBack,
  setFormData,
  getOrgRefetch,
  energyPricePriceZone,
  isAddPriceConfigPending,
  isAddPriceConfigSuccess,
  isAttachPriceConfigPending,
  isAttachPriceConfigSuccess,
}) => {
  const {colors} = useTheme()
  const {
    priceConfig,
    siteConfigurationDataToEdit,
    editConfigModeEnabled,
    authToken,
    setResetForms,
    setSiteConfigurationDataToEdit,
    setFluxVisionTuning,
    fluxVisionTuning,
  } = usePriceConfigurationContext();
  const { setToastMessage, setToastOpen } =
    useAppContextProvider();

  const [userEnteredSiteValue, setUserEnteredSiteValue] =
    useState("");

  const {
    data: siteLists,
    isLoading: isSiteGroupDataLoading,
    isFetching: isSiteGroupDataFetching,
    isFetched: isSiteGroupDataFetched,
    refetch: siteListRefetch,
  } = useGetSiteLists({
    authToken,
    leftPanelOpen: true,
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      siteConfig:
        priceConfig?.siteConfig ||
        formData?.siteConfig ||
        [],
      priceConfigName:
        priceConfig?.priceConfigName ||
        formData?.priceConfigName ||
        "",
    },
  });

  console.log(
    siteConfigurationDataToEdit,
    "siteConfigurationDataToEdit"
  );

  useEffect(() => {
    if (siteConfigurationDataToEdit) {
      setValue(
        "siteConfig",
        Array.isArray(
          siteConfigurationDataToEdit?.siteList
        ) && !isEmpty(siteConfigurationDataToEdit.siteList)
          ? siteConfigurationDataToEdit.siteList
          : []
      );
      setValue(
        "priceConfigName",
        siteConfigurationDataToEdit?.priceConfigName ??
          formData?.priceConfigName ??
          ""
      );
    }
  }, [siteConfigurationDataToEdit, setValue, formData]);

  const selectedOption = watch("siteConfig");
  const priceConfigName = watch("priceConfigName");

  const siteOptions =
    siteLists &&
    siteLists?.status === "ok" &&
    siteLists?.siteList?.length > 0
      ? siteLists?.siteList.map((site) => site.siteName)
      : [];

  const labelFormData = [
    {
      propertyName: "independentSystemOperator",
      value: formData?.ISO,
      label: "Independent System Operator",
    },
    {
      propertyName: "powerPurchaseAgreement",
      value: formData?.PPAType,
      label: "Power Purchase Agreement",
    },
    ...(formData?.PPAType &&
    formData?.PPAType?.toLowerCase() === "index"
      ? [
          {
            propertyName: "prizeZone",
            value: formData?.PriceZone,
            label: "Price Zone",
          },
        ]
      : []),
    ...(formData?.EnergyPrice
      ? [
          {
            propertyName: "costOfElectricity",
            value:
              formData?.PPAType?.toLowerCase() === "index"
                ? energyPricePriceZone
                  ? energyPricePriceZone
                  : formData?.EnergyPrice
                : formData?.EnergyPrice,
            label:
              "Electricity Cost per MW per hour (in USD)",
          },
        ]
      : []),
    ...(formData?.TunePreference === "Energy Optimizer" &&
    formData?.PPAType &&
    formData?.PPAType?.toLowerCase() === "index"
      ? [
          {
            propertyName: "energyOptimizer",
            value:
              formData?.TunePreference ===
              "Energy Optimizer"
                ? "True"
                : "False",
            label: "Energy Optimizer enabled",
          },
          {
            propertyName: "turnOnPrice",
            value: formData?.EnergyPriceLowerThreshold,
            label: "Turn On Price (in USD)",
          },
          {
            propertyName: "turnOffPrice",
            value: formData?.EnergyPriceUpperThreshold,
            label: "Turn Off Price (in USD)",
          },
        ]
      : []),
  ];

  const onSubmit = (data) => {
    setResetForms((prev) => [...prev, reset]);
    onOverallSubmit(data);
  };

  const {
    mutate: detachSiteFromPriceConfig,
    isPending,
    isSuccess,
  } = useAttachPriceConfig();

  const handleSiteConfigDeletion = (optionToDelete) => {
    console.log(
      editConfigModeEnabled,
      siteConfigurationDataToEdit?.siteList,
      "optionToDelete"
    );
    if (
      editConfigModeEnabled &&
      Array.isArray(siteConfigurationDataToEdit.siteList) &&
      !siteConfigurationDataToEdit.siteList?.includes(
        optionToDelete
      )
    ) {
      const newValue = selectedOption.filter(
        (option) => option !== optionToDelete
      );
      setValue("siteConfig", newValue);
    } else if (!editConfigModeEnabled) {
      const newValue = selectedOption.filter(
        (option) => option !== optionToDelete
      );
      setValue("siteConfig", newValue);
    } else {
      detachSiteFromPriceConfig(
        {
          attach: false,
          config_name:
            siteConfigurationDataToEdit?.priceConfigName,
          config_type: "priceConfig",
          sites: [optionToDelete],
        },
        {
          onSuccess: async (response) => {
            if (response?.status === "ok") {
              setToastMessage(
                `${optionToDelete} detached from price configuration successfully`
              );
              setToastOpen(true);
              const newValue = (
                watch("siteConfig") || []
              ).filter((site) => site !== optionToDelete);
              setValue("siteConfig", newValue, {
                shouldValidate: true,
                shouldDirty: true,
              });
              setSiteConfigurationDataToEdit((prev) => ({
                ...prev,
                siteList: prev?.siteList?.filter(
                  (site) => site !== optionToDelete
                ),
              }));
              setFormData((prev) => ({
                ...prev,
                siteLists: prev?.siteLists?.filter(
                  (site) => site !== optionToDelete
                ),
              }));
              await getOrgRefetch();
            } else {
              setToastMessage(
                `Failed to detach ${optionToDelete} from price configuration`
              );
              setToastOpen(true);
            }
          },
          onError: (error) => {
            setToastMessage(
              `Failed to detach ${optionToDelete} from price configuration`
            );
            setToastOpen(true);
            console.error(
              "Error detaching site from price configuration:",
              error
            );
          },
        }
      );
    }
  };

  const onFluxVisionTuningChange = (event) => {
    setFluxVisionTuning(event.target.checked);
  };

  return (
    <>
      <PriceConfigurationFormHeader
        setFormData={setFormData}
      />
      <DialogContent sx={{ padding: 0 }}>
        <Box
          display="flex"
          flexDirection={"column"}
          gap={8}
          paddingTop={"2.5rem"}
          paddingBottom={"2.5rem"}
        >
          <H8TitleMedium>
            Review the following details for your new price
            configuration and select the sites you want to
            assign it to.
          </H8TitleMedium>
          <Box
            display={"grid"}
            gridTemplateColumns={"1fr 2fr"}
            padding={"0.5rem"}
            borderRadius={"0.5rem"}
            sx={{ backgroundColor: colors.background.paper }}
            gap={6}
          >
            {labelFormData.map((item, index) => (
              <>
                <H9TitleSmall>{item?.label}</H9TitleSmall>
                <BodyLarge>{item?.value}</BodyLarge>
              </>
            ))}
          </Box>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box
              display={"flex"}
              flexDirection={"column"}
              gap={8}
            >
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="priceConfigName"
              >
                <H8TitleMedium>
                  Enter a price configuration name
                </H8TitleMedium>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                >
                  <StyledFormControl colors={colors}
                    fullWidth
                    margin="none"
                    error={!!errors.priceConfigName}
                  >
                    <Controller
                      name="priceConfigName"
                      control={control}
                      defaultValue={null}
                      rules={{
                        required:
                          "Please name your price configuration",
                        maxLength: {
                          value: 512,
                          message:
                            "Price Configuration name must be 512 characters or fewer",
                        },
                      }}
                      render={({ field, fieldState }) => (
                        <StyledTextField colors={colors}
                          {...field}
                          disabled={editConfigModeEnabled}
                          id="site-configuration-name"
                          label="Price configuration name"
                          variant="outlined"
                          error={!!fieldState.error}
                          helperText={
                            fieldState.error
                              ? fieldState.error.message
                              : ""
                          }
                          onBlur={(e) => {
                            const trimmedValue =
                              e.target.value.trim();
                            field.onChange(trimmedValue);
                            field.onBlur();
                          }}
                          fullWidth
                          style={{ minHeight: "2.5rem" }}
                        />
                      )}
                    />
                  </StyledFormControl>
                </Box>
              </Box>
              <Box
                display={"flex"}
                flexDirection={"column"}
                gap={4}
                className="sitesContainer"
              >
                <Box
                  display="flex"
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <H8TitleMedium>
                    Select sites to associate with this
                    price profile
                  </H8TitleMedium>
                </Box>
                <Box
                  display="flex"
                  flexDirection="row"
                  flexGrow={1}
                >
                  <StyledFormControl colors={colors}
                    fullWidth
                    margin="none"
                  >
                    {/* <InputLabel htmlFor="">Status</InputLabel> */}
                    <Controller
                      name="siteConfig"
                      control={control}
                      defaultValue={null}
                      render={({
                        field: { onChange, value, ref },
                      }) => (
                        <Autocomplete
                          fullWidth
                          multiple
                          inputValue={userEnteredSiteValue}
                          onInputChange={(
                            event,
                            inputValue,
                            reason
                          ) => {
                            if (reason === "input") {
                              setUserEnteredSiteValue(
                                inputValue
                              );
                            }
                          }}
                          onClose={(_, reason) => {
                            if (
                              reason === "blur" ||
                              reason === "escape"
                            ) {
                              setUserEnteredSiteValue("");
                            }
                          }}
                          disableCloseOnSelect
                          onChange={(_, data) =>
                            onChange(data)
                          }
                          getOptionDisabled={(option) =>
                            editConfigModeEnabled &&
                            siteConfigurationDataToEdit?.siteList &&
                            siteConfigurationDataToEdit.siteList.includes(
                              option
                            )
                          }
                          ListboxProps={{
                            sx: {
                              maxHeight: "250px",
                              overflowY: "auto",
                              backgroundColor: colors.background.paper,
                                color: colors.text.primary,
                                "&.selected": {
                                  backgroundColor:
                                    colors.primary.light,
                                },
                            },
                          }}
                          slotProps={{
                            popupIndicator: {
                              sx: { color: colors.text.muted , 
                              '&:hover': { color: colors.text.primary },
                            }
                            },
                            clearIndicator: {
                              sx: {
                                color: colors.text.muted,
                                '&:hover': { color: colors.text.primary },
                              },
                            },
                          }}
                          value={value || []}
                          clearIcon={null}
                          options={siteOptions} // Replace with your options
                          renderInput={(params) => (
                            <StyledTextField colors={colors}
                              {...params}
                              label="Site Name"
                              variant="outlined"
                              inputRef={ref}
                              sx={{ minHeight: "2.5rem" }}
                            />
                          )}
                          renderTags={() => null}
                        />
                      )}
                    />
                  </StyledFormControl>
                </Box>
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  gap={4}
                  maxWidth={"62.5rem"}
                  flexWrap={"wrap"}
                >
                  {selectedOption?.map((site, index) => (
                    <PlaceHolderButton
                      key={index}
                      onClose={() =>
                        handleSiteConfigDeletion(site)
                      }
                      text={site}
                    />
                  ))}
                </Box>
                {selectedOption &&
                  selectedOption.length > 0 && (
                    <Box
                      display={"flex"}
                      flexDirection={"column"}
                      gap={2}
                      paddingRight={"0.5rem"}
                    >
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        justifyContent={"space-between"}
                        flexGrow={1}
                        alignItems={"center"}
                      >
                        <H8TitleMedium>
                          {" "}
                          FluxVision Tuning
                        </H8TitleMedium>
                        <StyledSwitch
                          colors={colors}
                          checked={fluxVisionTuning}
                          onChange={
                            onFluxVisionTuningChange
                          }
                          defaultChecked={true}
                        />
                      </Box>
                      <Box
                        display={"flex"}
                        flexDirection={"row"}
                        alignItems={"center"}
                        gap={2}
                      >
                        {/* <span className="material-symbols-outlined">
                report
              </span> */}
                        {fluxVisionTuning ? (
                          <BodyMedium>
                            FluxVision Tuning mode is
                            enabled, meaning this command
                            will override local tuning
                            settings.
                          </BodyMedium>
                        ) : (
                          <BodyMedium>
                            FluxVision Tuning mode is
                            disabled, meaning this command
                            may be overridden by local
                            tuning settings.
                          </BodyMedium>
                        )}
                      </Box>
                    </Box>
                  )}
              </Box>
            </Box>
          </form>
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton colors={colors} onClick={onBack}>
          Back
        </SecondaryButton>
        <PrimaryButton colors={colors}
          onClick={handleSubmit(onSubmit)}
          disabled={
            isAddPriceConfigPending ||
            isAttachPriceConfigPending
          }
          endIcon={
            (isAddPriceConfigPending ||
              isAttachPriceConfigPending) && (
              <CircularProgress size={24} sx={{ color: colors.primary.main }} />
            )
          }
        >
          {isAddPriceConfigPending ||
          isAttachPriceConfigPending
            ? "Submitting..."
            : "Submit"}
        </PrimaryButton>
      </DialogActions>
    </>
  );
};

const AddPriceConfigDialogModal = (props) => {
  const { refetch: getOrgRefetch } = props;
  const {
    openAddPriceConfigModal,
    setOpenAddPriceConfigModal,
    mockData,
    setMockData,
    activeStep,
    setActiveStep,
    totalSteps,
    setTotalSteps,
    initialFormDataForAddingPriceConfig,
    editConfigModeEnabled,
    setEditConfigModeEnabled,
    siteConfigurationDataToEdit,
    setSiteConfigurationDataToEdit,
    authToken,
    setAuthToken,
    setResetForms,
    setFormData,
    setFluxVisionTuning,
    fluxVisionTuning,
  } = usePriceConfigurationContext();
  const { colors } = useTheme();

  const { getAccessTokenSilently, isAuthenticated } =
    useAuth0();

  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!isAuthenticated) {
          setAuthToken("");
          console.warn(
            "User is not authenticated. Fetching token..."
          );
          return;
        }
        const token = await getAccessTokenSilently({
          cacheMode: "cache-only",
          ignoreCache: true,
        });
        setAuthToken(token);
      } catch (error) {
        console.error(" Error fetching token:", error);
      }
    };
    fetchToken();
    const interval = setInterval(() => {
      fetchToken();
    }, 5 * 60 * 1000); // Refresh every 15 minutes

    return () => clearInterval(interval); // Cleanup on unmount
  }, [getAccessTokenSilently, isAuthenticated]);

  const [configFormData, setConfigFormData] = useState(
    initialFormDataForAddingPriceConfig
  );
  const { setToastMessage, setToastOpen, resetForms } =
    useAppContextProvider();

  const {
    mutate: addPriceConfig,
    isPending: isAddPriceConfigPending,
    isSuccess: isAddPriceConfigSuccess,
  } = useAddPriceConfig();
  const {
    mutate: attachPriceConfig,
    isPending: isAttachPriceConfigPending,
    isSuccess: isAttachPriceConfigSuccess,
  } = useAttachPriceConfig();

  const onMoveForward = (data) => {
    setConfigFormData({ ...configFormData, ...data });
    setActiveStep(activeStep + 1);
  };

  const [openPriceDialog, setOpenPriceDialog] =
    useState(false);
  const [arrayMessage, setArrayMessage] = useState([]);
  const [energyPricePriceZone, setEnergyPricePriceZone] =
    useState("");

  const onSubmit = (data) => {
    const finalData = Object.fromEntries(
      Object.entries(configFormData)
        .filter(
          ([key, value]) =>
            key !== "siteConfig" &&
            key !== "siteList" &&
            key !== "priceConfigName" &&
            value !== undefined &&
            value !== null &&
            value !== ""
        )
        .map(([key, value]) => {
          if (
            key === "RampUpTime" ||
            key === "RampDownTime" ||
            key === "EnergyPriceLowerThreshold" ||
            key === "EnergyPriceUpperThreshold" ||
            key === "EnergyPrice"
          ) {
            return [key, parseFloat(value)];
          }
          return [key, value];
        })
    );
    console.log(finalData, "finalData");
    const configName = editConfigModeEnabled
      ? siteConfigurationDataToEdit.priceConfigName
      : data?.priceConfigName;

    addPriceConfig(
      {
        config: finalData,
        config_type: "priceConfig",
        config_name: configName,
        ...(!editConfigModeEnabled && { operation: "add" }),
      },
      {
        onSuccess: async (response) => {
          if (response?.status === "ok") {
            const sites = data?.siteConfig;
            const message = editConfigModeEnabled
              ? `Price configuration updated successfully.`
              : `Price configuration added successfully.`;
            let attachSuccessMessage = "";
            if (
              data.siteConfig &&
              data.siteConfig.length > 0
            ) {
              attachPriceConfig(
                {
                  attach: true,
                  config_name: configName,
                  config_type: "priceConfig",
                  sites: sites,
                  fvTuning: fluxVisionTuning,
                },
                {
                  onSuccess: async (response) => {
                    if (response?.status === "ok") {
                      await getOrgRefetch();
                      if (response?.msg) {
                        if (
                          Array.isArray(response.msg) &&
                          response.msg.length !== 0
                        ) {
                          setArrayMessage(response.msg);
                          setOpenPriceDialog(true);
                          setOpenAddPriceConfigModal(false);
                        } else {
                          attachSuccessMessage =
                            response.msg;
                          setOpenAddPriceConfigModal(false);
                        }
                      }
                      if (attachSuccessMessage !== "") {
                        setToastOpen(true);
                        setToastMessage(
                          message +
                            " " +
                            attachSuccessMessage
                        );
                      }
                      if (Array.isArray(resetForms)) {
                        resetForms.forEach((reset) => {
                          reset();
                        });
                      }
                      setConfigFormData({});
                      setActiveStep(0);
                      setTotalSteps(4);
                      setSiteConfigurationDataToEdit({});
                      setEditConfigModeEnabled(false);
                      setResetForms([]);
                    } else {
                      setToastMessage(
                        `Error: ${response?.msg}`
                      );
                      setToastOpen(true);
                    }
                  },
                  onError: (error) => {
                    setToastMessage(
                      "Error attaching price configuration,Error:" +
                        error
                    );
                    setToastOpen(true);
                  },
                }
              );
            } else {
              await getOrgRefetch();
              setToastOpen(true);
              setToastMessage(message);
              setOpenAddPriceConfigModal(false);
              setConfigFormData({});
              if (Array.isArray(resetForms)) {
                resetForms.forEach((reset) => {
                  reset();
                });
              }
              setActiveStep(0);
              setTotalSteps(4);
              setResetForms([]);
              setConfigFormData({});
              setSiteConfigurationDataToEdit({});
              setEditConfigModeEnabled(false);
            }
          } else {
            setToastMessage(
              response?.msg
                ? response.msg
                : "Error adding price configuration"
            );
            setToastOpen(true);
            setOpenAddPriceConfigModal(false);
            setConfigFormData({});
            if (Array.isArray(resetForms)) {
              resetForms.forEach((reset) => {
                reset();
              });
            }
            setActiveStep(0);
            setTotalSteps(4);
            setResetForms([]);
          }
        },
        onError: (error) => {
          setToastMessage(
            "Error adding price configuration, Error:" +
              error
          );
          setToastOpen(true);
          setOpenAddPriceConfigModal(false);
          setConfigFormData({});
          if (Array.isArray(resetForms)) {
            resetForms.forEach((reset) => {
              reset();
            });
          }
          setActiveStep(0);
          setTotalSteps(4);
          setResetForms([]);
        },
      }
    );
    setFluxVisionTuning(true);
  };

  const onMoveBackward = () => {
    setActiveStep(activeStep - 1);
  };

  const independentSystemOperator = configFormData?.ISO;

  const progress = (activeStep / totalSteps) * 100;

  return (
    <>
      <StyledDialog
        colors={colors}
        maxWidth={"lg"}
        fullWidth
        open={openAddPriceConfigModal}
        hideBackdrop
      >
        <StyledLinearProgress
          colors={colors}
          variant="determinate"
          value={progress}
          sx={{ marginBottom: "1rem" }}
        />
        {activeStep === 0 && (
          <SystemOperatorForm
            formData={configFormData}
            onNext={onMoveForward}
            setFormData={setConfigFormData}
            setActiveStep={setActiveStep}
          />
        )}
        {independentSystemOperator &&
        independentSystemOperator?.toLowerCase() ===
          "ercot" ? (
          <>
            {activeStep === 1 && (
              <PowerPurchaseAgreementForm
                formData={configFormData}
                onNext={onMoveForward}
                onBack={onMoveBackward}
                setFormData={setConfigFormData}
              />
            )}
            {activeStep === 2 && (
              <PriceZoneForm
                formData={configFormData}
                onNext={onMoveForward}
                onBack={onMoveBackward}
                setFormData={setConfigFormData}
                setEnergyPricePriceZone={
                  setEnergyPricePriceZone
                }
              />
            )}
            {activeStep === 3 && (
              <ReviewForm
                formData={configFormData}
                onSubmit={onSubmit}
                onBack={onMoveBackward}
                setFormData={setConfigFormData}
                getOrgRefetch={getOrgRefetch}
                energyPricePriceZone={energyPricePriceZone}
                isAddPriceConfigPending={
                  isAddPriceConfigPending
                }
                isAddPriceConfigSuccess={
                  isAddPriceConfigSuccess
                }
                isAttachPriceConfigPending={
                  isAttachPriceConfigPending
                }
                isAttachPriceConfigSuccess={
                  isAttachPriceConfigSuccess
                }
              />
            )}
          </>
        ) : (
          <>
            {activeStep === 1 && (
              <PriceZoneForm
                formData={configFormData}
                onNext={onMoveForward}
                onBack={onMoveBackward}
                setFormData={setConfigFormData}
                setEnergyPricePriceZone={
                  setEnergyPricePriceZone
                }
              />
            )}
            {activeStep === 2 && (
              <ReviewForm
                formData={configFormData}
                onSubmit={onSubmit}
                onBack={onMoveBackward}
                setFormData={setConfigFormData}
                getOrgRefetch={getOrgRefetch}
                energyPricePriceZone={energyPricePriceZone}
                isAddPriceConfigPending={
                  isAddPriceConfigPending
                }
                isAddPriceConfigSuccess={
                  isAddPriceConfigSuccess
                }
                isAttachPriceConfigPending={
                  isAttachPriceConfigPending
                }
                isAttachPriceConfigSuccess={
                  isAttachPriceConfigSuccess
                }
              />
            )}
          </>
        )}
      </StyledDialog>
      <PriceConfigSitesDialog
        openPriceDialog={openPriceDialog}
        setOpenPriceDialog={setOpenPriceDialog}
        arrayMessage={arrayMessage}
        setArrayMessage={setArrayMessage}
      />
    </>
  );
};

const AddPriceConfigDialog = (props) => {
  const { refetch } = props;
  return (
    <>
      <AddPriceConfigDialogModal refetch={refetch} />
    </>
  );
};

export default AddPriceConfigDialog;
